import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { CircularProgress } from '@mui/material';
import { PageLoader } from '../../../lib/loader';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetSingleCoin, useUpdateFee } from '../api/apiFeeManage';
import { ChevronLeft, Percent, CircleDollarSign } from 'lucide-react';
import { preventNonNumericalDotInput } from '../../../utils/formatAddress';


// Validation Schema
const schema = yup.object().shape({
  name: yup.string().required('Name is required'),
  sendFee: yup.number()
    .transform((value, originalValue) => originalValue === "" ? null : value)
    .required('Send Fee is required')
    .integer('Send Fee must be a whole number')
    .min(1, 'Send Fee must be at least 1')
    .max(100, 'Send Fee must not exceed 100'),
  internalFee: yup.number()
    .transform((value, originalValue) => originalValue === "" ? null : value)
    .required('Internal Fee is required')
    .integer('Internal Fee must be a whole number')
    .min(1, 'Internal Fee must be at least 1')
    .max(100, 'Internal Fee must not exceed 100'),
  minInternalTransfer: yup.number()
    .typeError('Invalid transfer amount')
    .required('Min Internal Transfer Fee is required')
    .positive('Must be greater than 0')
    .test('max-decimal-digits', 'Must have at most 6 digits after the decimal', (value) => {
      if (value === undefined || value === null) return true; // Allows empty values
      return /^\d+(\.\d{1,6})?$/.test(String(value)); // Ensures at least 1 digit before the dot, up to 6 digits after
    }),

  minTransfer: yup.number()
    .typeError('Invalid transfer amount')
    .required('Min Transfer Fee is required')
    .positive('Must be greater than 0')
    .test('max-decimal-digits', 'Must have at most 6 digits after the decimal', (value) => {
      if (value === undefined || value === null) return true; // Allows empty values
      return /^\d+(\.\d{1,6})?$/.test(String(value)); // Ensures at least 1 digit before the dot, up to 6 digits after
    }),
})

function EditCoin() {
  const navigate = useNavigate();
  const { id } = useParams()

  // Ensure editData is valid before making API call
  const { data: SingleCoin, isLoading } = useGetSingleCoin(id);

  const { control, handleSubmit, formState: { errors, isValid, isDirty }, reset, setValue } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      sendFee: '',
      internalFee: '',
      minInternalTransfer: '',
      minTransfer: '',
    }
  });

  useEffect(() => {
    setValuesFromAPI();
  }, [SingleCoin]);



  const setValuesFromAPI = () => {
    if (SingleCoin?.data?.crypto) {
      setValue('name', SingleCoin.data.crypto.name || '');
      setValue('sendFee', SingleCoin.data.crypto.sendFee || '');
      setValue('internalFee', SingleCoin.data.crypto.internalFee || '');
      setValue('minInternalTransfer', SingleCoin.data.crypto.minInternalTransfer || '');
      setValue('minTransfer', SingleCoin.data.crypto.minTransfer || '');
    }
  };

  const updateFee = useUpdateFee();

  const onSubmit = async (data) => {
    if (!SingleCoin.data) {
      return;
    }

    data._id = SingleCoin.data?.crypto._id;

    const res = await updateFee.mutateAsync({ ...data });
    if (res?.status) {
      reset();
      navigate('/fee-management');
    }
  };

  return (
    <div className="p-4" style={{ minWidth: "40vw" }}>
      <div className='d-flex justify-content-between align-items-center mb-3'>
        <h2>Edit Coin</h2>
        <ChevronLeft size={18} className="me-2" onClick={() => navigate('/fee-management')} style={{ cursor: "pointer" }} />
      </div>
      <hr />
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {isLoading ? (
          <PageLoader />
        ) : (
          <>
            <div className='mb-3'>
              <h6 className="pb-2">Coin Name</h6>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <input {...field} readOnly className="form-control" placeholder="Enter coin name" disabled />
                )}
              />
              {errors.name && <p className="errMsg">{errors.name.message}</p>}
            </div>

            <div className='mb-3'>
              <h6 className="pb-2">Withdraw Fee (%)</h6>
              <div className="input-group">
                <Controller
                  name="sendFee"
                  onKeyPress={(e) => preventNonNumericalDotInput(e)}
                  control={control}
                  render={({ field }) => (
                    <input {...field} type="number" className="form-control" placeholder="Enter send fee" min="1" max="100" />
                  )}
                />
                <button type="button" className="btn p-0 input-group-icon border-0">
                  <Percent size={20} color='#aaa9a9' />
                </button>
              </div>
              {errors.sendFee && <p className="errMsg">{errors.sendFee.message}</p>}
            </div>



            <div className='mb-3'>
              <h6 className="pb-2">Min Internal Transfer</h6>
              <div className="input-group">
                <button type="button" className="btn p-0 input-group-icon border-0">
                  <CircleDollarSign size={20} color='#aaa9a9' />
                </button>
                <Controller
                  name="minInternalTransfer"
                  control={control}
                  render={({ field }) => (
                    <input {...field} type="number" className="form-control" placeholder="Enter min internal transfer" min="0.001" />
                  )}
                />
              </div>
              {errors.minInternalTransfer && <p className="errMsg">{errors.minInternalTransfer.message}</p>}
            </div>

            <div className='mb-3'>
              <h6 className="pb-2">Min Withdraw Transfer</h6>
              <div className="input-group">
                <button type="button" className="btn p-0 input-group-icon border-0">
                  <CircleDollarSign size={20} color='#aaa9a9' />
                </button>
                <Controller
                  name="minTransfer"
                  control={control}
                  render={({ field }) => (
                    <input {...field} type="number" className="form-control" placeholder="Enter min transfer" min="0.0001" />
                  )}
                />
              </div>
              {errors.minTransfer && <p className="errMsg">{errors.minTransfer.message}</p>}
            </div>



            <div className="d-flex justify-content-center rounded-pill">
              <button type="submit" className="btn btn-primary d-flex align-items-center" disabled={updateFee.isLoading || !isValid || !isDirty}>
                {updateFee.isLoading ? <CircularProgress size={20} style={{ color: "#fff" }} className='mx-4' /> : "Submit"}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}



export default EditCoin;
